import * as React from 'react';
import { Drawer, Input, Select, Table } from '@revfluence/fresh';
import { UserAvatar } from '@frontend/app/components/UserAvatar';
import styles from './MetaItems.module.scss';

const PERMISSION_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Draft', value: 'meta_ads:draft' },
  { label: 'Publish', value: 'meta_ads:publish' },
];

function getPermissionValue(permissions: string[] = []): 'none' | 'meta_ads:draft' | 'meta_ads:publish' {
  if (permissions.includes('meta_ads:publish')) return 'meta_ads:publish';
  if (permissions.includes('meta_ads:draft')) return 'meta_ads:draft';
  return 'none';
}

function getPermissionsFromValue(value: string): string[] {
  if (value === 'meta_ads:publish') return ['meta_ads:publish', 'meta_ads:draft'];
  if (value === 'meta_ads:draft') return ['meta_ads:draft'];
  return [];
}

interface IAdPublishersDrawerProps {
  open: boolean;
  onClose: () => void;
  users: Array<{
    id: string;
    name: string;
    email: string;
    permissions?: string[];
    profileImageUrl?: string;
  }>;
  currentUserId: string;
  isManagerAdmin: boolean;
  onPermissionsChange: (userId: string, permissions: string[]) => void;
}

const AdPublishersDrawer: React.FC<IAdPublishersDrawerProps> = ({
  open,
  onClose,
  users,
  currentUserId,
  isManagerAdmin,
  onPermissionsChange,
}) => {
  const [search, setSearch] = React.useState('');

  const filteredUsers = React.useMemo(() => {
    if (!search) return users;
    const lower = search.toLowerCase();
    return users.filter((u) => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
  }, [users, search]);

  return (
    <Drawer title="Edit Ad Publishers" open={open} onClose={onClose} width={600}>
      <div className={styles.adPublishersDrawerDescription}>
        Manage which team members can publish ads from this account. By default, team members do not have ad publishing
        permissions.
      </div>
      {/* Summary bar for publish/draft counts */}
      {(() => {
        const publishCount = users.filter((u) => (u.permissions || []).includes('meta_ads:publish')).length;
        const draftOnlyCount = users.filter(
          (u) =>
            (u.permissions || []).includes('meta_ads:draft') && !(u.permissions || []).includes('meta_ads:publish'),
        ).length;
        return (
          <div className="flex justify-end items-center text-xs text-gray-500 my-2">
            <span>{publishCount} can publish</span>
            <span className="mx-2">•</span>
            <span>{draftOnlyCount} can draft only</span>
          </div>
        );
      })()}
      <Input.Search
        placeholder="Search by name or email"
        className={styles.adPublishersDrawerSearch}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Table
        dataSource={filteredUsers.map((u, i) => ({
          key: u.id || i,
          ...u,
        }))}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name ',
            key: 'name',
            width: 180,
            render: (text, record) => {
              const isCurrentUser = record.id === currentUserId;
              return (
                <div className={styles.adPublishersDrawerNameCell}>
                  <UserAvatar
                    name={record.name}
                    profilePicture={record.profileImageUrl ?? ''}
                    size={32}
                  />
                  <div className={styles.adPublishersDrawerNameTextWrap}>
                    <span className={styles.adPublishersDrawerNameText} title={text}>
                      {text}
                    </span>
                    {isCurrentUser && <span className={styles.adPublishersDrawerCurrentUser}>(you)</span>}
                  </div>
                </div>
              );
            },
          },
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
          },
          {
            title: 'Ads Permission',
            dataIndex: 'permissions',
            key: 'permissions',
            render: (_value, record) => {
              const value = getPermissionValue(record.permissions || []);
              return (
                <Select
                  value={value}
                  style={{ width: 100 }}
                  options={PERMISSION_OPTIONS}
                  disabled={!isManagerAdmin}
                  onChange={(val) => onPermissionsChange(record.id, getPermissionsFromValue(val))}
                />
              );
            },
          },
        ]}
        pagination={false}
        size="small"
      />
    </Drawer>
  );
};

export default AdPublishersDrawer;
